import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';

// Theme colors - centralized color palette
const colors = {
  // Primary colors
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#017aff',
    600: '#0163cc',
    700: '#014d99',
    800: '#003366',
    900: '#001a33',
  },

  // Secondary colors
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Success colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  // Warning colors
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  // Error colors
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // Neutral colors
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Semantic colors
  background: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9',
  },

  text: {
    primary: '#0f172a',
    secondary: '#475569',
    tertiary: '#64748b',
    inverse: '#ffffff',
  },

  border: {
    light: '#e2e8f0',
    medium: '#cbd5e1',
    dark: '#94a3b8',
  },

  breadcrumb: '#26629e',
};

// Theme configuration
const lightTheme = {
  colors,
  mode: 'light',
};

const darkTheme = {
  colors: {
    ...colors,
    background: {
      primary: '#0f172a',
      secondary: '#1e293b',
      tertiary: '#334155',
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
      tertiary: '#94a3b8',
      inverse: '#0f172a',
    },
    border: {
      light: '#334155',
      medium: '#475569',
      dark: '#64748b',
    },
    breadcrumb: '#7dd3fc',
  },
  mode: 'dark',
};

const ThemeContext = createContext({
  theme: lightTheme,
  toggleTheme: () => {},
  isDark: false,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    // Safely check if we're in a browser environment
    if (typeof window === 'undefined') {
      return false; // Default to light theme in non-browser environments
    }

    // Check localStorage for saved theme preference
    // Default to light theme if no preference saved
    try {
      const savedTheme = localStorage.getItem('theme');
      return savedTheme === 'dark';
    } catch {
      // localStorage might not be available (e.g., in incognito mode)
      // Silently fail
    }

    // Default to light mode for new users
    return false;
  });

  const theme = isDark ? darkTheme : lightTheme;
  const isUpdatingRef = useRef(false);

  // Listen for theme change events across apps (custom event for same-window sync)
  useEffect(() => {
    const handleThemeChange = (e) => {
      if (e.detail && e.detail.theme && !isUpdatingRef.current) {
        setIsDark(e.detail.theme === 'dark');
      }
    };

    window.addEventListener('themeChange', handleThemeChange);

    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, []);

  useEffect(() => {
    // Safely save theme preference to localStorage
    try {
      isUpdatingRef.current = true;
      localStorage.setItem('theme', isDark ? 'dark' : 'light');

      // Dispatch custom event to sync across apps in the same window
      const event = new CustomEvent('themeChange', {
        detail: { theme: isDark ? 'dark' : 'light' },
      });
      window.dispatchEvent(event);

      // Reset flag after a brief delay
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    } catch {
      // localStorage might not be available - silently fail
      isUpdatingRef.current = false;
    }

    // Update CSS custom properties for theme
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.colors.primary[500]);
    root.style.setProperty('--color-secondary', theme.colors.secondary[500]);
    root.style.setProperty('--color-success', theme.colors.success[500]);
    root.style.setProperty('--color-warning', theme.colors.warning[500]);
    root.style.setProperty('--color-error', theme.colors.error[500]);
    root.style.setProperty(
      '--color-background-primary',
      theme.colors.background.primary,
    );
    root.style.setProperty(
      '--color-background-secondary',
      theme.colors.background.secondary,
    );
    root.style.setProperty('--color-text-primary', theme.colors.text.primary);
    root.style.setProperty(
      '--color-text-secondary',
      theme.colors.text.secondary,
    );
    root.style.setProperty('--color-text-tertiary', theme.colors.text.tertiary);
    root.style.setProperty('--color-text-inverse', theme.colors.text.inverse);
    root.style.setProperty('--color-border-light', theme.colors.border.light);
    root.style.setProperty('--color-border-medium', theme.colors.border.medium);
    root.style.setProperty('--color-border-dark', theme.colors.border.dark);
    root.style.setProperty(
      '--color-background-tertiary',
      theme.colors.background.tertiary,
    );

    // Additional theme color shades
    root.style.setProperty('--color-primary-50', theme.colors.primary[50]);
    root.style.setProperty('--color-primary-100', theme.colors.primary[100]);
    root.style.setProperty('--color-primary-200', theme.colors.primary[200]);
    root.style.setProperty('--color-primary-300', theme.colors.primary[300]);
    root.style.setProperty('--color-primary-400', theme.colors.primary[400]);
    root.style.setProperty('--color-primary-500', theme.colors.primary[500]);
    root.style.setProperty('--color-primary-600', theme.colors.primary[600]);
    root.style.setProperty('--color-primary-700', theme.colors.primary[700]);
    root.style.setProperty('--color-error-50', theme.colors.error[50]);
    root.style.setProperty('--color-error-200', theme.colors.error[200]);
    root.style.setProperty('--color-error-600', theme.colors.error[600]);
    root.style.setProperty('--color-error-700', theme.colors.error[700]);
    root.style.setProperty('--color-success-50', theme.colors.success[50]);
    root.style.setProperty('--color-success-200', theme.colors.success[200]);
    root.style.setProperty('--color-success-500', theme.colors.success[500]);
    root.style.setProperty('--color-success-600', theme.colors.success[600]);
    root.style.setProperty('--color-success-700', theme.colors.success[700]);
    root.style.setProperty('--color-success-800', theme.colors.success[800]);

    // Warning color shades
    root.style.setProperty('--color-warning-50', theme.colors.warning[50]);
    root.style.setProperty('--color-warning-200', theme.colors.warning[200]);
    root.style.setProperty('--color-warning-400', theme.colors.warning[400]);
    root.style.setProperty('--color-success-600', theme.colors.success[600]);
    root.style.setProperty('--color-warning-50', theme.colors.warning[50]);
    root.style.setProperty('--color-warning-200', theme.colors.warning[200]);
    root.style.setProperty('--color-warning-300', theme.colors.warning[300]);
    root.style.setProperty('--color-warning-400', theme.colors.warning[400]);

    // Status colors
    root.style.setProperty(
      '--color-status-draft',
      isDark ? '#9ca3af' : '#c8c8c8',
    );
    root.style.setProperty(
      '--color-status-published',
      theme.colors.success[400],
    );

    // Breadcrumb colors
    root.style.setProperty('--color-breadcrumb', theme.colors.breadcrumb);
  }, [isDark, theme]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({
      theme,
      toggleTheme,
      isDark,
    }),
    [theme, toggleTheme, isDark],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
